import { MarkupError, type MarkupPosition } from "./errors.js";

/**
 * A parsed attribute, kept with its own position.
 *
 * The position is the attribute's name, not the element's, because that is what an error about
 * a bad value needs to point at: a window with forty attributes reported at the element would
 * send the reader to the wrong end of a long line.
 */
export interface XmlAttribute extends MarkupPosition {
  name: string;
  value: string;
}

export interface XmlElement extends MarkupPosition {
  name: string;
  attributes: XmlAttribute[];
  children: XmlElement[];
  /** Text content, entities resolved and whitespace collapsed. Empty when there is none. */
  text: string;
}

const ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
};

const NAME_START = /[A-Za-z_]/;
const NAME_PART = /[-A-Za-z0-9_.]/;

/**
 * A small, strict XML reader.
 *
 * Hand-written rather than `DOMParser` for two reasons that pull the same way: the library has
 * no runtime dependencies and no DOM to borrow in a test, and a parser of our own can say
 * *where* something went wrong in the terms the writer used. It is strict XML — quoted
 * attribute values, matched tags, the five named entities and numeric character references —
 * because markup that an ordinary XML editor can also validate is worth more than markup that
 * saves a few keystrokes.
 *
 * What it leaves out, deliberately: namespaces, DTDs, entity declarations, processing
 * instructions beyond skipping them, and mixed content. A control tree has no use for any of it.
 */
class Reader {
  #text: string;
  #at = 0;
  #line = 1;
  #lineStart = 0;

  constructor(text: string) {
    // A byte-order mark at the front is not markup and not an error either.
    this.#text = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  }

  get position(): MarkupPosition {
    return { line: this.#line, col: this.#at - this.#lineStart + 1 };
  }

  get done(): boolean {
    return this.#at >= this.#text.length;
  }

  peek(offset = 0): string {
    return this.#text[this.#at + offset] ?? "";
  }

  startsWith(s: string): boolean {
    return this.#text.startsWith(s, this.#at);
  }

  advance(count = 1): string {
    const taken = this.#text.slice(this.#at, this.#at + count);
    for (const ch of taken) {
      if (ch === "\n") {
        this.#line++;
        this.#lineStart = this.#at + 1;
      }
      this.#at++;
    }
    return taken;
  }

  /** Everything up to `stop`, which is left unconsumed. Throws if it never comes. */
  until(stop: string, what: string): string {
    const found = this.#text.indexOf(stop, this.#at);
    if (found < 0) this.fail(`unterminated ${what}: expected ${stop}`);
    return this.advance(found - this.#at);
  }

  skipSpace(): void {
    while (!this.done && /\s/.test(this.peek())) this.advance();
  }

  fail(message: string, at: MarkupPosition = this.position): never {
    throw new MarkupError(message, at);
  }
}

/**
 * Resolve the five named entities and numeric character references.
 *
 * Every `&` is an entity or a mistake — there is no third reading in XML — so the scan matches
 * the ampersand itself rather than the shapes that follow it. Matching only the valid shapes
 * would let `Save & Close` through as literal text, which is exactly the case worth catching.
 */
function decode(text: string, reader: Reader, at: MarkupPosition): string {
  if (!text.includes("&")) return text;
  return text.replace(/&([^;&<\s]*)(;?)/g, (match, body: string, terminator: string) => {
    if (body === "" || terminator === "") {
      reader.fail(`a bare "&" must be written "&amp;" (found "${match}")`, at);
    }
    if (body.startsWith("#")) {
      const isHex = body[1] === "x" || body[1] === "X";
      const digits = isHex ? body.slice(2) : body.slice(1);
      const code = Number.parseInt(digits, isHex ? 16 : 10);
      if (!/^[0-9A-Fa-f]+$/.test(digits) || !Number.isFinite(code) || code > 0x10ffff) {
        reader.fail(`"${match}" is not a character reference`, at);
      }
      return String.fromCodePoint(code);
    }
    const named = ENTITIES[body];
    if (named === undefined) {
      reader.fail(`unknown entity "${match}": only &amp; &lt; &gt; &quot; and &apos; exist`, at);
    }
    return named;
  });
}

function readName(reader: Reader, what: string): string {
  const at = reader.position;
  if (!NAME_START.test(reader.peek())) reader.fail(`expected ${what}`, at);
  let name = reader.advance();
  while (!reader.done && NAME_PART.test(reader.peek())) name += reader.advance();
  return name;
}

/** Comments, CDATA and processing instructions: skipped, but not silently unterminated. */
function skipMisc(reader: Reader): boolean {
  if (reader.startsWith("<!--")) {
    reader.advance(4);
    reader.until("-->", "comment");
    reader.advance(3);
    return true;
  }
  if (reader.startsWith("<?")) {
    reader.advance(2);
    reader.until("?>", "processing instruction");
    reader.advance(2);
    return true;
  }
  if (reader.startsWith("<!DOCTYPE")) {
    reader.advance(9);
    reader.until(">", "doctype");
    reader.advance(1);
    return true;
  }
  return false;
}

function readAttributes(reader: Reader, element: string): XmlAttribute[] {
  const attributes: XmlAttribute[] = [];
  const seen = new Set<string>();
  for (;;) {
    reader.skipSpace();
    const ch = reader.peek();
    if (ch === ">" || ch === "/" || ch === "") return attributes;

    const at = reader.position;
    const name = readName(reader, `an attribute name in <${element}>`);
    if (seen.has(name)) reader.fail(`<${element}> already has a "${name}" attribute`, at);
    seen.add(name);

    reader.skipSpace();
    if (reader.peek() !== "=") {
      reader.fail(`"${name}" needs a value, written ${name}="…"`, at);
    }
    reader.advance();
    reader.skipSpace();

    const quote = reader.peek();
    if (quote !== '"' && quote !== "'") {
      reader.fail(`the value of "${name}" must be quoted`, reader.position);
    }
    reader.advance();
    const valueAt = reader.position;
    const raw = reader.until(quote, `value for "${name}"`);
    reader.advance();
    if (raw.includes("<")) reader.fail(`"<" is not allowed in an attribute; write &lt;`, valueAt);
    attributes.push({ name, value: decode(raw, reader, valueAt), ...at });
  }
}

/** Collapse runs of whitespace to one space, the way a paragraph of markup reads. */
function collapse(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

function readElement(reader: Reader): XmlElement {
  const at = reader.position;
  reader.advance(); // "<"
  const name = readName(reader, "an element name after \"<\"");
  const attributes = readAttributes(reader, name);

  const element: XmlElement = { name, attributes, children: [], text: "", ...at };

  reader.skipSpace();
  if (reader.startsWith("/>")) {
    reader.advance(2);
    return element;
  }
  if (reader.peek() !== ">") reader.fail(`expected ">" to close the <${name}> tag`);
  reader.advance();

  // Text is accumulated across the whole element: a caption split around a comment is one
  // caption, and CDATA joins it verbatim rather than being collapsed with it.
  let loose = "";
  const parts: string[] = [];
  const flush = (): void => {
    const collapsed = collapse(loose);
    if (collapsed) parts.push(collapsed);
    loose = "";
  };

  for (;;) {
    if (reader.done) reader.fail(`<${name}> was never closed`, at);

    if (reader.startsWith("</")) {
      const closeAt = reader.position;
      reader.advance(2);
      const closing = readName(reader, "an element name after \"</\"");
      reader.skipSpace();
      if (reader.peek() !== ">") reader.fail(`expected ">" to close </${closing}>`);
      reader.advance();
      // Reported at the tag that was left open rather than at the one that closed it: the
      // stray tag is where the parser noticed, but the missing one is what has to be written.
      if (closing !== name) {
        reader.fail(`<${name}> was never closed: found </${closing}> instead`, at);
      }
      void closeAt;
      flush();
      element.text = parts.join(" ");
      return element;
    }

    if (reader.startsWith("<![CDATA[")) {
      reader.advance(9);
      const verbatim = reader.until("]]>", "CDATA section");
      reader.advance(3);
      flush();
      if (verbatim) parts.push(verbatim);
      continue;
    }

    if (skipMisc(reader)) continue;

    if (reader.peek() === "<") {
      element.children.push(readElement(reader));
      continue;
    }

    const textAt = reader.position;
    let chunk = "";
    while (!reader.done && reader.peek() !== "<") chunk += reader.advance();
    loose += decode(chunk, reader, textAt);
  }
}

/**
 * Parse a document and return its root element.
 *
 * Exported in its own right, not only as a step inside `loadMarkup`: a tool that wants to read a
 * layout without building controls — a formatter, a linter, a converter — needs the tree and
 * nothing else, and a parser that can be tested without a desktop is a parser that gets tested.
 */
export function parseMarkup(xml: string): XmlElement {
  const reader = new Reader(xml);
  for (;;) {
    reader.skipSpace();
    if (reader.done) reader.fail("the document is empty: it needs one root element");
    if (skipMisc(reader)) continue;
    if (reader.peek() !== "<") reader.fail("expected an element, starting with \"<\"");
    break;
  }

  const root = readElement(reader);

  for (;;) {
    reader.skipSpace();
    if (reader.done) return root;
    if (skipMisc(reader)) continue;
    reader.fail(`only one root element is allowed; <${root.name}> is already the root`);
  }
}
